import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ChannelSelectionService } from '../../../services/channel-selection-service';
import { MassNotification, MassNotificationSeverity, MassNotificationTemplate } from '../../../store/communications';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { QueryParams, Workflow, WorkflowParentType, WorkflowStep, WorkflowStepAction } from 'src/app/modules/core/store/models';
import { CommunicationsApiService } from '../../../store/communications/communications-api.service';
import { Observable, Subject, of } from 'rxjs';
import { WorkflowApiService } from '../../../store/workflow/workflow-api.service';
import { CultureService } from 'src/app/modules/core/services/culture.service';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { Store } from '@ngrx/store';
import { NotificationStatusService } from '../../../services/notification-status-service';
import { OrgBusinessUnit } from '@app/modules/core/org-business-units/org-business-unit.model';
import { OrgBusinessUnitsApiService } from '@app/modules/core/org-business-units/org-business-units-api.service';
import { UserContextService } from '@app/modules/core/services/user-context.service';
import { RatingScaleLevel } from '@app/modules/management/store/ratings/ratings.model';

@Component({
    selector: 'topo-comms-notification-details',
    templateUrl: './notification-details.component.html',
    styleUrls: ['./notification-details.component.scss'],
    standalone: false
})
export class NotificationDetailsComponent implements OnInit, OnDestroy {
  @Input() set notification(value: MassNotification) {
    this._notification = value;
    if (!!value) {
      this.ratingScaleLevel = this.notificationStatusService.getNotificationRatingLevelItem(value.notificationSeverity);
    }
    this.loadNotificationWorkflowTemplates(value);
    this.changeDetector.markForCheck();
  }
  _notification: MassNotification;
  get notification(): MassNotification {
    return this._notification;
  }

  @Input() editMode: boolean;
  @Output() notificationWorkflowLoaded = new EventEmitter<Workflow>();
  @Output() recipientWorkflowLoaded = new EventEmitter<Workflow>();

  @Output() notificationNavigateRequested = new EventEmitter<WorkflowStepAction>();

  destroy$ = new Subject<void>();
  notificationTemplate: MassNotificationTemplate;
  workflows: Workflow[];
  logNotification: MassNotification;
//  severityItem: { severity: MassNotificationSeverity; backColor: string; textColor: string };
  ratingScaleLevel: RatingScaleLevel;
  businessUnits: OrgBusinessUnit[];
  selectedUnitUIds = [];
  hasBusinessUnits: boolean;

  public notificationWorkflow: Workflow;
  public recipientWorkflow: Workflow;

  constructor(
    private store: Store<any>,
    private translateService: TranslateService,
    private commApiService: CommunicationsApiService,
    private workflowApiService: WorkflowApiService,
    private cultureService: CultureService,
    private notificationStatusService: NotificationStatusService,
    private changeDetector: ChangeDetectorRef,
    private orgBusinessUnitsApiService: OrgBusinessUnitsApiService,
    private userContextService: UserContextService
  ) {}

  ngOnInit(): void {
    // called when signalR updates the stats
    this.store
      .select(fromComm.selectSelectedMassNotificationStats)
      .pipe(takeUntil(this.destroy$))
      .subscribe(stats => {
        if (!!stats && this._notification?.uId === stats?.notification.uId) {
          if (!!stats?.notification) {
            this._notification = stats.notification;
            this.changeDetector.detectChanges();
          }
        }
      });

      this.orgBusinessUnitsApiService.hasOrgBusinessUnits().subscribe(hasBusinessUnits => {
        this.hasBusinessUnits = hasBusinessUnits;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadNotificationWorkflowTemplates(notification: MassNotification): void {
    this.commApiService.getMassNotificationTemplate(notification.notificationTemplateUId).subscribe(template => {
      this.notificationTemplate = template;

      this.orgBusinessUnitsApiService
        .getOrgBusinessUnitsForUser(this.userContextService.getUserContext().userProfileUId)
        .subscribe(businessUnits => {
          this.processBusinessUnits(businessUnits);
        });

      const params: QueryParams = {
        skip: 0,
        take: 10,
        where: `IsActive=true && (UId=Guid.Parse("${template.massNotificationWorkflowUId}") OR UId=Guid.Parse("${template.massNotificationRecipientsWorkflowUId}"))`,
        sortField: 'Name',
        sortDirection: 'Ascending'
      };

      this.workflowApiService.getWorkflows(params, true).subscribe(workflows => {
        this.workflows = workflows?.data;

        this.notificationWorkflow = this.workflows.find(w => w.parentType === WorkflowParentType.MassNotification);
        this.recipientWorkflow = this.workflows.find(w => w.parentType === WorkflowParentType.MassNotificationRecipient);

        this.notificationWorkflowLoaded.emit(this.notificationWorkflow);
        this.recipientWorkflowLoaded.emit(this.recipientWorkflow);
        this.changeDetector.detectChanges();
      });
    });
  }

  public isSafetyCheckType(): boolean {
    return this.notificationTemplate?.type === fromComm.MassNotificationType.SafetyCheck;
  }

  public getDateDisplay(date: any): string {
    return this.cultureService.getDateDisplay(date, 'LONG-DATE-WITH-TIME');
  }

  onWorkflowStepChanged(workflowStep: WorkflowStep): void {
    const updatedNotification = {
      ...this.notification,
      workflowStepUId: workflowStep.uId
    };

    this.store.dispatch(fromComm.updateMassNotification({ notification: updatedNotification, close: false }));
  }

  private processBusinessUnits(businessUnits: OrgBusinessUnit[]): void {
    const rootUnit = businessUnits?.find(u => !!!u.parentUId);
    this.selectedUnitUIds = [];
    if (!!rootUnit) {
      rootUnit.name = this.translateService.instant('MANAGEMENT.MENU.GLOBAL-UNIT', { name: rootUnit.name });
    }
    if (!!this.notificationTemplate) {
      if (this.notificationTemplate.businessUnitUIds?.length > 0) {
        this.selectedUnitUIds = this.notificationTemplate.businessUnitUIds.sort((c1, c2) =>
          businessUnits.find(b => b.uId == c1)?.level > businessUnits.find(b => b.uId == c2)?.level ? 1 : -1
        );
      } else {
        if (!!rootUnit?.uId) {
          this.selectedUnitUIds = [rootUnit.uId];
        }
      }
    }
    this.businessUnits = businessUnits;
  }

  logClick(): void {
    this.logNotification = this._notification;
  }
  onLogClosed(): void {
    this.logNotification = null;
  }
}
